import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionService, { Subscription, LikeQuota, Product } from '../services/SubscriptionService';
import './SubscriptionManagement.css';

const SubscriptionManagement: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [likeQuota, setLikeQuota] = useState<LikeQuota | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subData, quotaData, productsData] = await Promise.all([
        SubscriptionService.getCurrentSubscription(),
        SubscriptionService.getLikeQuota(),
        SubscriptionService.getProducts()
      ]);
      setSubscription(subData);
      setLikeQuota(quotaData);
      setProducts(productsData);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return;
    }

    try {
      await SubscriptionService.cancelSubscription();
      alert('Subscription cancelled successfully. You will keep access until the end of your billing period.');
      loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await SubscriptionService.reactivateSubscription();
      alert('Subscription reactivated successfully!');
      loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reactivate subscription');
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handlePurchaseBoost = async (productId: string) => {
    try {
      await SubscriptionService.createPurchaseCheckout(productId);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to purchase boost');
    }
  };

  if (loading) {
    return <div className="subscription-container"><div className="loading">Loading...</div></div>;
  }

  if (!subscription) {
    return <div className="subscription-container"><div className="error">Failed to load subscription</div></div>;
  }

  const tier = subscription.tier;
  const isPaid = tier.price > 0;
  const isFree = tier.name === 'free';

  return (
    <div className="subscription-container">
      <h1>Subscription Management</h1>

      {/* Current Plan Card */}
      <div className="subscription-card">
        <div className="subscription-header">
          <div>
            <h2>{tier.displayName} Plan</h2>
            {isPaid && (
              <div className="subscription-price">
                ${tier.price}/month
              </div>
            )}
          </div>
          <div className={`status-badge ${subscription.status}`}>
            {subscription.status === 'active' ? '✓ Active' : subscription.status.toUpperCase()}
          </div>
        </div>

        <div className="subscription-features">
          <h3>Your Benefits:</h3>
          <ul>
            {tier.features.map((feature, index) => (
              <li key={index}>
                <span className="checkmark">✓</span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {subscription.cancelAtPeriodEnd && (
          <div className="cancellation-notice">
            ⚠️ Your subscription will be cancelled on {new Date(subscription.currentPeriodEnd!).toLocaleDateString()}
          </div>
        )}

        {isPaid && subscription.currentPeriodEnd && (
          <div className="billing-info">
            <p><strong>Next billing date:</strong> {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</p>
          </div>
        )}

        <div className="subscription-actions">
          {isFree ? (
            <button className="upgrade-button" onClick={handleUpgrade}>
              🚀 Upgrade to Premium
            </button>
          ) : (
            <>
              {!subscription.cancelAtPeriodEnd ? (
                <>
                  <button className="upgrade-button" onClick={handleUpgrade}>
                    ⬆️ Change Plan
                  </button>
                  <button className="cancel-button" onClick={handleCancelSubscription}>
                    Cancel Subscription
                  </button>
                </>
              ) : (
                <button className="reactivate-button" onClick={handleReactivateSubscription}>
                  ↻ Reactivate Subscription
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Usage Stats */}
      {likeQuota && (
        <div className="usage-card">
          <h3>Today's Usage</h3>
          <div className="usage-stats">
            <div className="usage-stat">
              <div className="stat-label">Likes Remaining</div>
              <div className="stat-value">
                {likeQuota.unlimited ? '∞ Unlimited' : `${likeQuota.remaining} / ${likeQuota.limit}`}
              </div>
            </div>
            {!likeQuota.unlimited && likeQuota.remaining === 0 && (
              <div className="usage-warning">
                ⚠️ You've reached your daily limit. Upgrade for more likes!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Boosts Section */}
      <div className="boosts-section">
        <h3>Profile Boosts</h3>
        <p>Get more visibility and matches with a profile boost!</p>

        <div className="products-grid">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <h4>{product.name}</h4>
              <p className="product-description">{product.description}</p>
              <div className="product-price">${product.price.toFixed(2)}</div>
              <button
                className="purchase-button"
                onClick={() => handlePurchaseBoost(product.id)}
              >
                Purchase Boost
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
